import { Add, Microphone, Upload } from '@tunasong/icons'
import type { Entity, Persisted } from '@tunasong/schemas'
import {
  Fab,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  useUploadFiles,
} from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, ReactNode } from 'react'
import { useRef, useState } from 'react'
import { addMediaLabels } from './add-media-labels.js'
import { RecordDialog } from './record-dialog.js'
import styles from './add-media-button.module.css'

/** A host's own menu item, after Record and Upload audio (a song's Add chord, GH #2488) */
export interface AddMediaItem {
  label: string
  icon?: ReactNode
  onSelect: () => void
  /** Shown disabled with this reason under its name, e.g. "Tap where the chord goes first" */
  disabledReason?: string
}

/**
 * The host's items, read when the menu opens: the popup mounts on open, so a function sees the state of that
 * moment (a song's caret), not of the last render.
 */
const HostItems: FC<{ items: AddMediaItem[] | (() => AddMediaItem[]) }> = ({ items }) =>
  (typeof items === 'function' ? items() : items).map(item => (
    <MenuItem
      key={item.label}
      disabled={Boolean(item.disabledReason)}
      onClick={item.disabledReason ? undefined : item.onSelect}
    >
      {item.icon ? <ListItemIcon>{item.icon}</ListItemIcon> : null}
      <ListItemText primary={item.label} secondary={item.disabledReason} />
    </MenuItem>
  ))

export interface AddMediaButtonProps {
  /** Where recordings and uploads are added */
  parent: Persisted<Entity>
  /**
   * Offer Record next to Upload (a menu). Without it the FAB is Upload, one tap, no menu: recording into a
   * setlist has no meaning, and a menu of one is a needless step.
   */
  record?: boolean
  /** The menu FAB's name; defaults to "Record or upload audio" */
  label?: string
  /**
   * More menu items after Record and Upload audio, or a function read when the menu opens. Only with `record`: the
   * single-action FAB has no menu
   */
  items?: AddMediaItem[] | (() => AddMediaItem[])
  className?: string
}

/**
 * Mobile: one Add button for an entity's media, replacing the separate Upload, Record and Edit FABs (Edit
 * is in the header). Fixed above the bottom navigation. Record opens the record dialog; Upload opens the
 * audio file picker, and the upload manager reports the outcome. Focus returns to the button when the menu
 * closes (Base UI).
 *
 * Songs and pages use the menu; setlists use the single action (GH #2420).
 */
export const AddMediaButton: FC<AddMediaButtonProps> = ({
  parent,
  record = true,
  label = addMediaLabels.addWithRecord,
  items = [],
  className,
}) => {
  const [recordOpen, setRecordOpen] = useState(false)
  const uploadRef = useRef<HTMLInputElement | null>(null)
  const { handleChange } = useUploadFiles()

  const picker = (
    <input ref={uploadRef} type="file" multiple accept="audio/*" hidden onChange={handleChange(parent.id)} />
  )

  if (!record) {
    return (
      <>
        <Fab
          color="primary"
          className={cn(styles.add, className)}
          aria-label={addMediaLabels.upload}
          onClick={() => uploadRef.current?.click()}
        >
          <Upload />
        </Fab>
        {picker}
      </>
    )
  }

  return (
    <>
      <Menu>
        <MenuTrigger
          render={
            <Fab color="primary" className={cn(styles.add, className)} aria-label={label}>
              <Add />
            </Fab>
          }
        />
        <MenuContent>
          <MenuItem onClick={() => setRecordOpen(true)}>
            <ListItemIcon>
              <Microphone />
            </ListItemIcon>
            <ListItemText primary={addMediaLabels.record} />
          </MenuItem>
          <MenuItem onClick={() => uploadRef.current?.click()}>
            <ListItemIcon>
              <Upload />
            </ListItemIcon>
            <ListItemText primary={addMediaLabels.upload} />
          </MenuItem>
          <HostItems items={items} />
        </MenuContent>
      </Menu>
      {picker}
      <RecordDialog open={recordOpen} onClose={() => setRecordOpen(false)} parent={parent} />
    </>
  )
}
